import * as actionTypes from "./actionTypes";

const initialState = {
  loading: false,
  error: null,
  invoiceHistory: {
    error: null,
    loading: false,
    activeRequestId: null,
    activeDocumentType: null,
    orderDetails: {
      columns: [],
      headers: [],
      dataSource: [],
      total: 0
    },
    filters: {
      startDate: null,
      endDate: null,
      abn: null,
      vin: null
    },
    pagination: { pageSize: 20, currentPage: 1 }
  },
  vendor: {
    error: null,
    loading: false,
    pagination: { pageSize: 20, currentPage: 1, total: 20 },
    searchText: "",
    vendors: []
  },
  vehicle: {
    error: null,
    loading: false,
    vehicleDetails: null
  }
};

const setVendorPageSize = (state, action) => {
  const {
    orderDetails: { totalCars },
    pagination: { currentPage }
  } = state;
  const lastPage = totalCars / action.data;

  return {
    ...state,
    vendor: {
      ...state.vendor,
      searchText: action.data,
      pagination: {
        ...state.pagination,
        pageSize: action.data,
        currentPage: lastPage > currentPage ? currentPage : Math.ceil(lastPage)
      }
    }
  };
};

const setVendorSearchInput = (state, action) => ({
  ...state,
  vendor: {
    ...state.vendor,
    searchText: action.data,
    pagination: {
      ...state.vendor.pagination,
      currentPage: 1
    }
  }
});

const setVendorCurrentPage = (state, action) => ({
  ...state,
  vendor: {
    ...state.vendor,
    pagination: {
      ...state.vendor.pagination,
      currentPage: action.data
    }
  }
});

const fetchVendorDataStart = (state) => ({
  ...state,
  vendor: {
    ...state.vendor,
    loading: true
  }
});

const fetchVendorDataSuccess = (state, action) => ({
  ...state,
  vendor: {
    ...state.vendor,
    loading: false,
    vendors: action.data.dataSource || [],
    pagination: {
      ...state.vendor.pagination,
      pageSize: action.data.pageSize,
      total: action.data.totalCars
    }
  }
});

const fetchVendorDataFail = (state) => ({
  ...state,
  vendor: {
    ...state.vendor,
    loading: false
  }
});

const fetchVehicleDetailsStart = (state) => ({
  ...state,
  vehicle: {
    ...state.vehicle,
    loading: true
  }
});

const fetchVehicleDetailsSuccess = (state, action) => ({
  ...state,
  vehicle: {
    ...state.vehicle,
    loading: false,
    vehicleDetails: action.data || {}
  }
});

const fetchVehicleDetailsFail = (state) => ({
  ...state,
  vehicle: {
    ...state.vehicle,
    loading: false
  }
});

const resetVehicleDetails = (state) => ({
  ...state,
  vehicle: {
    ...state.vehicle,
    error: null,
    loading: false,
    vehicleDetails: null
  }
});

const generateInvoiceStart = (state) => ({
  ...state,
  loading: true,
  error: null
});

const generateInvoiceSuccess = (state) => ({
  ...state,
  loading: false
});

const generateInvoiceFail = (state, action) => ({
  ...state,
  loading: false,
  error: action.error
});

const setInvoiceFilters = (state, action) => ({
  ...state,
  invoiceHistory: {
    ...state.invoiceHistory,
    filters: {
      ...state.invoiceHistory.filters,
      ...action.data
    },
    pagination: {
      ...state.invoiceHistory.pagination,
      currentPage: 1
    }
  }
});

const setInvoiceHistoryCurrentPage = (state, action) => ({
  ...state,
  invoiceHistory: {
    ...state.invoiceHistory,
    pagination: {
      ...state.invoiceHistory.pagination,
      currentPage: action.data
    }
  }
});

const setInvoiceHistoryPageSize = (state, action) => ({
  ...state,
  invoiceHistory: {
    ...state.invoiceHistory,
    pagination: {
      ...state.invoiceHistory.pagination,
      currentPage: 1,
      pageSize: action.data
    }
  }
});

const setInvoiceHistoryDocumentType = (state, action) => ({
  ...state,
  invoiceHistory: {
    ...state.invoiceHistory,
    activeDocumentType: action.documentType
  }
});

const isStaleInvoiceHistoryResponse = (state, action) =>
  (state.invoiceHistory.activeDocumentType && action.documentType !== state.invoiceHistory.activeDocumentType) ||
  (state.invoiceHistory.activeRequestId && action.requestId !== state.invoiceHistory.activeRequestId);

const fetchInvoiceHistoryStart = (state, action) => {
  if (state.invoiceHistory.activeDocumentType && action.documentType !== state.invoiceHistory.activeDocumentType) {
    return state;
  }

  return {
    ...state,
    invoiceHistory: { ...state.invoiceHistory, loading: true, error: null, activeRequestId: action.requestId }
  };
};

const fetchInvoiceHistorySuccess = (state, action) => {
  if (isStaleInvoiceHistoryResponse(state, action)) {
    return state;
  }

  return {
    ...state,
    invoiceHistory: {
      ...state.invoiceHistory,
      loading: false,
      orderDetails: action.data,
      error: null
    }
  };
};

const fetchInvoiceHistoryFail = (state, action) => {
  if (isStaleInvoiceHistoryResponse(state, action)) {
    return state;
  }

  return {
    ...state,
    invoiceHistory: {
      ...state.invoiceHistory,
      loading: false,
      error: action.error
    }
  };
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.GET_VENDOR_DATA_START:
      return fetchVendorDataStart(state, action);
    case actionTypes.GET_VENDOR_DATA_SUCCESS:
      return fetchVendorDataSuccess(state, action);
    case actionTypes.GET_VENDOR_DATA_FAIL:
      return fetchVendorDataFail(state, action);
    case actionTypes.SET_VENDOR_SEARCH_INPUT:
      return setVendorSearchInput(state, action);
    case actionTypes.SET_VENDOR_PAGE_SIZE:
      return setVendorPageSize(state, action);
    case actionTypes.SET_VENDOR_CURRENT_PAGE:
      return setVendorCurrentPage(state, action);
    case actionTypes.FETCH_VEHICLE_DETAILS_START:
      return fetchVehicleDetailsStart(state, action);
    case actionTypes.FETCH_VEHICLE_DETAILS_SUCCESS:
      return fetchVehicleDetailsSuccess(state, action);
    case actionTypes.FETCH_VEHICLE_DETAILS_FAIL:
      return fetchVehicleDetailsFail(state, action);
    case actionTypes.RESET_VEHICLE_DETAILS:
      return resetVehicleDetails(state);
    case actionTypes.GENERATE_INVOICE_START:
      return generateInvoiceStart(state);
    case actionTypes.GENERATE_INVOICE_SUCCESS:
      return generateInvoiceSuccess(state, action);
    case actionTypes.GENERATE_INVOICE_FAIL:
      return generateInvoiceFail(state, action);
    case actionTypes.SET_INVOICE_HISTORY_PAGE_SIZE:
      return setInvoiceHistoryPageSize(state, action);
    case actionTypes.SET_INVOICE_HISTORY_CURRENT_PAGE:
      return setInvoiceHistoryCurrentPage(state, action);
    case actionTypes.SET_INVOICE_FILTERS:
      return setInvoiceFilters(state, action);
    case actionTypes.SET_INVOICE_HISTORY_DOCUMENT_TYPE:
      return setInvoiceHistoryDocumentType(state, action);
    case actionTypes.FETCH_INVOICE_HISTORY_START:
      return fetchInvoiceHistoryStart(state, action);
    case actionTypes.FETCH_INVOICE_HISTORY_SUCCESS:
      return fetchInvoiceHistorySuccess(state, action);
    case actionTypes.FETCH_INVOICE_HISTORY_FAIL:
      return fetchInvoiceHistoryFail(state, action);
    default:
      return state;
  }
};

export default reducer;
